/**
 * Resolving `meta.roles[entityId]` from an entity read.
 *
 * The API returns the viewer's own role(s) on an entity alongside the
 * permission flags — `meta.roles[id]` is an array, because one user can hold
 * several roles on the same team (a coach who also plays). This mirrors the
 * permission resolver: the answer arrives with the entity, so no second
 * request is needed just to say who the viewer is.
 */

/** One role entry as the API returns it in `meta.roles[id]`. */
export interface EntityRoleEntry {
  role_id: number
  role_name: string
}

/** A role resolved for display. */
export interface ResolvedRole {
  /** Stable key + colour lookup key. `getRoleColors` takes this. */
  id: string
  /** Human label, straight from the API. */
  name: string
}

/**
 * The viewer's roles on an entity, ready to render.
 *
 * Defensive on shape rather than on access: an absent or malformed `roles`
 * map yields an empty list, and a caller rendering badges simply shows none.
 * Unlike a permission gate there is nothing to deny here — an unknown role is
 * only ever a missing badge.
 *
 * Entries missing a usable id or name are dropped, and duplicate role ids are
 * collapsed so a repeated grant renders one badge.
 */
export const resolveEntityRoles = (
  roles: Record<string, EntityRoleEntry[]> | null | undefined,
  entityId: string | null | undefined
): ResolvedRole[] => {
  if (!roles || !entityId) {
    return []
  }

  const entries = roles[entityId]
  if (!Array.isArray(entries)) {
    return []
  }

  const seen = new Set<string>()
  const resolved: ResolvedRole[] = []

  entries.forEach((entry) => {
    if (!entry || entry.role_id === null || entry.role_id === undefined) {
      return
    }

    const id = String(entry.role_id)
    const name =
      typeof entry.role_name === 'string' ? entry.role_name.trim() : ''

    if (!name || seen.has(id)) {
      return
    }

    seen.add(id)
    resolved.push({ id, name })
  })

  return resolved
}
